// Relatórios do Bem-Estar (0.43): frequência e constância de cada seção nos últimos 7 ou 30 dias.
import { useState } from 'react';
import { ScrollView, Text, View } from 'react-native';
import { useDoc } from '../../store/store';
import { coposDo, frequencia } from '../../store/bemestar';
import { Chip, Sheet } from '../ui';
import { C, alpha } from '../theme';
import { addDays, parseYmd, today } from '../../lib/date';
import { t } from '../../lib/i18n';

const ml = (v: number) => (v >= 1000 ? `${String(Math.round(v / 100) / 10).replace('.', ',')} L` : `${Math.round(v)} ml`);

function Barra({ nome, icon, feitos, previstos, cor }: { nome: string; icon: string; feitos: number; previstos: number; cor: string }) {
  const p = previstos ? feitos / previstos : 0;
  return (
    <View style={{ gap: 4 }}>
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
        <Text style={{ fontSize: 16 }}>{icon}</Text>
        <Text style={{ color: C.text, flex: 1 }} numberOfLines={1}>{nome}</Text>
        <Text style={{ color: p >= 0.8 ? C.ok : p >= 0.5 ? C.warn : C.sub, fontWeight: '700', fontSize: 12 }}>
          {previstos ? t('{n} de {total} dias', { n: feitos, total: previstos }) : t('sem dias previstos')}</Text>
      </View>
      <View style={{ height: 6, borderRadius: 3, backgroundColor: C.card2, overflow: 'hidden' }}>
        <View style={{ width: `${Math.round(p * 100)}%`, height: '100%', backgroundColor: cor }} />
      </View>
    </View>
  );
}
const Bloco = ({ titulo, children }: { titulo: string; children: React.ReactNode }) => (
  <View style={{ backgroundColor: C.card, borderRadius: 16, padding: 14, gap: 10 }}>
    <Text style={{ color: C.sub, fontSize: 12, fontWeight: '700', letterSpacing: 0.8 }}>{titulo}</Text>
    {children}
  </View>
);
const Num = ({ v, rot, cor }: { v: string; rot: string; cor: string }) => (
  <View style={{ flex: 1, backgroundColor: alpha(cor, 0.12), borderRadius: 12, padding: 10, gap: 2 }}>
    <Text style={{ color: C.text, fontWeight: '900', fontSize: 18 }}>{v}</Text>
    <Text style={{ color: C.sub, fontSize: 11 }}>{rot}</Text>
  </View>
);

export function RelatoriosBemEstar({ visible, cor, onClose }: { visible: boolean; cor: string; onClose: () => void }) {
  const d = useDoc('bemestar');
  const [n, setN] = useState(7);
  const dias = Array.from({ length: n }, (_, i) => addDays(today(), -i));

  // Água
  const copoMl = d.copoMl ?? 250;
  const metaMl = d.metaMl ?? d.metaAgua * copoMl;
  const totais = dias.map(x => coposDo(d, x).reduce((s, v) => s + v, 0));
  const diasMeta = totais.filter(v => v >= metaMl).length;
  const media = totais.reduce((s, v) => s + v, 0) / n;

  // Alimentação: refeições feitas, dentro e fora do planejado
  let refPrev = 0, refFeitas = 0, fora = 0;
  for (const x of dias) {
    const dow = parseYmd(x).getDay();
    for (const r of d.refeicoes) {
      if (!r.dias.includes(dow)) continue;
      refPrev++;
      if ((d.feitos[x] ?? []).includes(r.id)) refFeitas++;
      if (r.alt?.[x]) fora++;
    }
  }

  // Exercícios
  const treinos = d.treinos ?? [];
  const treinosFeitos = dias.reduce((s, x) => s + treinos.filter(tr => (d.feitos[x] ?? []).includes(tr.id)).length, 0);
  const exFeitos = dias.reduce((s, x) => s + (d.feitosEx?.[x]?.length ?? 0), 0);

  // Atividade (relógio)
  const passos = dias.map(x => d.relogio?.[x]?.passos ?? 0).filter(Boolean);
  const metaP = d.metaPassos ?? 8000;

  return (
    <Sheet visible={visible} onClose={onClose} title={t('Relatórios do Bem-Estar')}>
      <ScrollView contentContainerStyle={{ padding: 16, gap: 12, paddingBottom: 60 }}>
        <View style={{ flexDirection: 'row', gap: 8 }}>
          <Chip cor={cor} on={n === 7} label={t('Últimos 7 dias')} onPress={() => setN(7)} />
          <Chip cor={cor} on={n === 30} label={t('Últimos 30 dias')} onPress={() => setN(30)} />
        </View>

        <Bloco titulo={'⌚ ' + t('ATIVIDADE FÍSICA')}>
          <View style={{ flexDirection: 'row', gap: 8 }}>
            <Num cor={cor} v={passos.length ? Math.round(passos.reduce((s, v) => s + v, 0) / passos.length).toLocaleString() : '-'} rot={t('passos por dia (média)')} />
            <Num cor={cor} v={`${passos.filter(p => p >= metaP).length}/${n}`} rot={t('dias com a meta de passos')} />
          </View>
          <View style={{ flexDirection: 'row', gap: 8 }}>
            <Num cor={cor} v={String(treinosFeitos)} rot={t('treinos concluídos')} />
            <Num cor={cor} v={String(exFeitos)} rot={t('exercícios concluídos')} />
          </View>
          {treinos.map(tr => { const f = frequencia(d, tr.id, tr.dias, n); return <Barra key={tr.id} nome={tr.nome} icon={tr.icon} feitos={f.feitos} previstos={f.previstos} cor={cor} />; })}
        </Bloco>

        <Bloco titulo={'💧 ' + t('ÁGUA')}>
          <View style={{ flexDirection: 'row', gap: 8 }}>
            <Num cor="#06B6D4" v={`${diasMeta}/${n}`} rot={t('dias com a meta batida')} />
            <Num cor="#06B6D4" v={ml(media)} rot={t('por dia (média)')} />
          </View>
        </Bloco>

        <Bloco titulo={'🍽️ ' + t('CRONOGRAMA ALIMENTAR')}>
          <View style={{ flexDirection: 'row', gap: 8 }}>
            <Num cor={cor} v={`${refFeitas}/${refPrev}`} rot={t('refeições feitas')} />
            <Num cor={C.warn} v={`${refPrev - fora}/${fora}`} rot={t('dentro / fora do planejado')} />
          </View>
          {d.refeicoes.map(r => { const f = frequencia(d, r.id, r.dias, n); return <Barra key={r.id} nome={r.nome} icon={r.icon} feitos={f.feitos} previstos={f.previstos} cor={cor} />; })}
        </Bloco>

        {d.cuidados.length > 0 && (
          <Bloco titulo={'🧴 ' + t('CUIDADOS PESSOAIS')}>
            {d.cuidados.map(c => { const f = frequencia(d, c.id, c.dias, n); return <Barra key={c.id} nome={c.nome} icon={c.icon} feitos={f.feitos} previstos={f.previstos} cor={cor} />; })}
          </Bloco>
        )}
        {(d.suplementos ?? []).length > 0 && (
          <Bloco titulo={'💊 ' + t('SUPLEMENTAÇÃO')}>
            {(d.suplementos ?? []).map(s => { const f = frequencia(d, s.id, s.dias, n); return <Barra key={s.id} nome={s.nome} icon={s.icon} feitos={f.feitos} previstos={f.previstos} cor={cor} />; })}
          </Bloco>
        )}
        <Text style={{ color: C.mute, fontSize: 11 }}>{t('Conta os dias em que cada item estava previsto (pelos dias da semana dele) e os que foram marcados como feitos.')}</Text>
      </ScrollView>
    </Sheet>
  );
}
